import { type FC, type ReactNode } from "react";

import { cx } from "@emotion/css";
import { UnstyledButton } from "@mantine/core";
import { CrossIcon } from "@snapchat/snap-design-system-icons";
import { clsx } from "clsx";

import { linkButtonBlackSmallClassName, linkButtonSmallClassName } from "../styles";

import { dismiss, inverted } from "./Banner.module.css";
import { BannerWrapper, Overlay, Subtitle, Title, bannerFit, bannerLink } from "./Banner.styles";
import { Picture } from "./Picture";
import { BannerDefaultCta } from "./messages.generated";

export type BannerDismissProps =
    | { onDismiss: () => void; dismissLabel: string }
    | { onDismiss?: never; dismissLabel?: never };

type BannerProps = {
    bannerContainerClassName?: string;
    webpImageUrl?: string;
    jpegImageUrl?: string;
    svgImageUrl?: string;
    titleText?: ReactNode;
    subtitleText?: ReactNode;
    ctaText?: ReactNode;
    ctaURL?: string;
    openInNewTab?: boolean;
    isInverted?: boolean;
    dataTestId?: string;
} & BannerDismissProps;

export const Banner: FC<BannerProps> = ({
    titleText,
    subtitleText,
    ctaText,
    ctaURL,
    openInNewTab = false,
    bannerContainerClassName,
    webpImageUrl,
    jpegImageUrl,
    svgImageUrl,
    isInverted,
    dataTestId,
    dismissLabel,
    onDismiss,
}) => {
    return (
        <BannerWrapper className={bannerContainerClassName} data-testid={dataTestId}>
            <Picture
                className={bannerFit}
                webpSrc={webpImageUrl}
                jpegSrc={jpegImageUrl}
                svgSrc={svgImageUrl}
                alt={"banner background"}
                dataTestId="banner-background"
            />
            {onDismiss && (
                <UnstyledButton
                    className={clsx(dismiss, isInverted && inverted)}
                    aria-label={dismissLabel}
                    onClick={onDismiss}
                    data-testid="banner-dismiss"
                >
                    <CrossIcon />
                </UnstyledButton>
            )}
            <Overlay>
                {titleText && (
                    <Title data-testid="banner-title" isInverted={isInverted}>
                        {titleText}
                    </Title>
                )}
                {subtitleText && (
                    <Subtitle data-testid="banner-subtitle" isInverted={isInverted}>
                        {subtitleText}
                    </Subtitle>
                )}

                {ctaURL && (
                    <a
                        data-tessid="banner-cta"
                        className={cx(
                            isInverted ? linkButtonBlackSmallClassName : linkButtonSmallClassName,
                            bannerLink,
                        )}
                        href={ctaURL}
                        target={openInNewTab ? "_blank" : "_self"}
                        rel="noopener noreferrer"
                    >
                        {ctaText ?? <BannerDefaultCta />}
                    </a>
                )}
            </Overlay>
        </BannerWrapper>
    );
};
